/**
 * 예정 모임 화면 — 시작 30분 전까지(=race window 진입 전) 노출.
 * 정기 모임(isRegularMeeting)은 본 화면 적용 외 (별도 로직).
 *
 * 핵심 가치: "확정시키기" — 일정·장소·참석을 굳히는 단계.
 *
 * 섹션:
 *  ① Hero — 카운트다운(D-N) + 일시·장소 한 줄
 *  ② 일정 상태 — 일정 미정/투표 중이면 CTA
 *  ③ 장소 카드 — 장소 설정 / 지도 미니뷰 (GPS 마커 미노출)
 *  ④ RSVP 현황 — 응답 n/m + 미응답자 칩
 *  ⑤ 공유/초대 — 카카오톡·링크·초대코드, 지난 모임에서 초대
 *  Footer — [편집] (주최자) [채팅]
 *
 * UI 아이콘은 모두 인라인 SVG (이모지 금지).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { localizePlaceName } from 'common/utils/localizePlaceName';
import { useNavigate, useLocation } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { MapContainer } from 'components/map';
import { MeetingTypeTag } from './MeetingTypeTag';
import { MeetingRepository } from 'repository/meetingRepository';
import { SubPlaceModal, SubPlace, SubPlaceForm } from './SubPlaceModal';
import { MeetingInviteSection } from './MeetingInviteSection';
import { localInputToUtc } from 'common/utils/datetime';
import { MemberActionMenu } from './MemberActionMenu';
import { IcHotel } from 'components/common/icons/EmojiIcons';
import { AppScreen, Section, SectionHeader, Bleed, Button, ButtonFooter, Badge, TimeBadge, Avatar, InverseBanner, ActionRow, ActionList, SwitchList, SwitchRow, SvgBase } from 'components/ds';
import styles from './MeetingUpcomingView.module.css';

// ── Inline SVG icons (Feather/Lucide style) ────────────────────────
const IconCalendar = (p: any) => (
    <SvgBase {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></SvgBase>
);
const IconPlus = (p: any) => (
    <SvgBase {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></SvgBase>
);

// ──────────────────────────────────────────────────────────────────

interface Props {
    meeting: any;
    members: any[]; // raw membersData.data
    participants: any[]; // mapped { id, name, status, ... }
    isCreator: boolean;
    isMember: boolean;
    meetingId: string;
    /** 시작 시각 ms — null 이면 일정 미정 */
    startMs: number | null;
    /** 모임의 sub_place 목록 — 추가 장소 시간 겹침 검증용 */
    subPlaces?: any[];
    isJoining: boolean;
    onJoinMeeting: () => void;
    onShare: () => void;
    onOpenInviteFromPast: () => void;
    onEdit: () => void;
    onChangeAttendance: () => void;
    /** 추천 장소 1클릭 적용 후 모임 상세 새로고침 트리거 */
    onMeetingChanged?: () => void;
    /** InviteFromPastMeetings 모달에서 초대된 userNo 들 — MeetingInviteSection 행이 "초대됨" 으로 즉시 갱신되도록 pass-through */
    externallyInvitedUserNos?: number[];
    /** 내 위치 공유 ON/OFF — 토글 UI 노출 (참석 상태에서만 활성) */
    isLocationSharing?: boolean;
    onToggleLocationSharing?: () => void;
    /** 내 참석 상태 — 'attending'/'not_attending'/'undecided' */
    attendanceStatus?: 'attending' | 'not_attending' | 'undecided';
    /** 여행지 좌표 — TRAVEL 모임의 sub-place 검색 시 anchor (해외에서 한국 디폴트로 가는 것 방지) */
    travelDestinationCenter?: { lat: number; lng: number } | null;
    /** 여행지 이름 — 호텔 검색 placeholder 등 */
    travelDestinationName?: string;
    /** 여행 기간 — 장소 추가 시 "방문 일차" 선택 칩 노출에 사용 */
    travelPeriod?: { days: number; nights: number; start: Date; end: Date } | null;
}

export const MeetingUpcomingView: React.FC<Props> = ({
    meeting,
    members,
    participants,
    isCreator,
    isMember,
    meetingId,
    startMs,
    isJoining,
    onJoinMeeting,
    onShare,
    onOpenInviteFromPast,
    onEdit,
    onChangeAttendance,
    onMeetingChanged,
    subPlaces = [],
    externallyInvitedUserNos,
    isLocationSharing,
    onToggleLocationSharing,
    attendanceStatus,
    travelDestinationCenter,
    travelDestinationName,
    travelPeriod,
}) => {
    const { t } = useTranslation();
    const isTravelMeeting = meeting?.meetingType === 'TRAVEL';
    const navigate = useNavigate();
    const routeLocation = useLocation();
    const { showAlert, showConfirm } = useAlert();
    const [clearingPlace, setClearingPlace] = useState(false);
    const [subPlaceReturnProcessed, setSubPlaceReturnProcessed] = useState(false);

    // 모임 삭제 (주최자) / 나가기 (멤버) — 일정 미정 모임을 삭제할 entry 가 없던 회귀 보강.
    // MeetingDetail 의 동일 로직과 같은 정책: 본인 외 참여자 중 '미정/참석' 1명이라도 있으면 삭제 차단.
    const [leavingOrDeleting, setLeavingOrDeleting] = useState(false);

    const handleDeleteMeeting = async () => {
        if (!meetingId || leavingOrDeleting) return;
        const blocking = participants.filter((p: any) => !p.isMe && p.status !== 'declined');
        if (blocking.length > 0) {
            await showAlert({
                message: t('meetingUpcoming.deleteBlocked', { count: blocking.length }),
            });
            return;
        }
        const ok = await showConfirm({
            message: t('meetingUpcoming.deleteConfirm'),
            confirmText: t('meetingUpcoming.deleteConfirmBtn'),
        });
        if (!ok) return;
        setLeavingOrDeleting(true);
        try {
            const res: any = await MeetingRepository.onDeleteMeeting(meetingId);
            const status = res?.status ?? 0;
            if (status >= 200 && status < 300) {
                showAlert({ message: t('meetingUpcoming.deleted') });
                navigate('/home', { replace: true });
            } else {
                await showAlert({ message: res?.data?.message || t('meetingUpcoming.deleteFail') });
            }
        } catch {
            await showAlert({ message: t('meetingUpcoming.deleteError') });
        } finally {
            setLeavingOrDeleting(false);
        }
    };

    const handleLeaveMeeting = async () => {
        if (!meetingId || leavingOrDeleting) return;
        const ok = await showConfirm({
            message: t('meetingUpcoming.leaveConfirm'),
            confirmText: t('meetingUpcoming.leaveBtn'),
        });
        if (!ok) return;
        setLeavingOrDeleting(true);
        try {
            const res: any = await MeetingRepository.onPostLeaveMeeting(meetingId);
            const status = res?.status ?? 0;
            if (status >= 200 && status < 300) {
                showAlert({ message: t('meetingUpcoming.left') });
                navigate('/home', { replace: true });
            } else {
                await showAlert({ message: res?.data?.message || t('meetingUpcoming.leaveFail') });
            }
        } catch {
            await showAlert({ message: t('meetingUpcoming.leaveError') });
        } finally {
            setLeavingOrDeleting(false);
        }
    };

    const handleClearPlace = async () => {
        if (!meetingId || clearingPlace) return;
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostChange') });
            return;
        }
        const ok = await showConfirm({
            title: t('meetingUpcoming.placeDeleteTitle'),
            message: t('meetingUpcoming.placeDeleteMsg'),
            confirmText: t('meetingUpcoming.delete'),
        });
        if (!ok) return;
        setClearingPlace(true);
        try {
            await MeetingRepository.onDeleteMeetingPlace(meetingId);
            showAlert({ message: t('meetingUpcoming.placeDeleted') });
            onMeetingChanged?.();
        } catch {
            showAlert({ message: t('meetingUpcoming.placeDeleteFail') });
        } finally {
            setClearingPlace(false);
        }
    };

    // 자주 함께한 사람 추천 / 초대 로직은 MeetingInviteSection 으로 이전.

    // 자주 갔던 장소 추천 — 장소가 미정일 때만 fetch
    const hasLocationSet = !!(meeting?.location && meeting?.latitude && meeting?.longitude);
    const [places, setPlaces] = useState<Array<{ placeId: string; placeName: string; placeAddress?: string; latitude?: string; longitude?: string; visitCount: number; overlapMemberCount: number }>>([]);
    const [placesLoading, setPlacesLoading] = useState(false);
    const [pickingPlaceId, setPickingPlaceId] = useState<string | null>(null);

    useEffect(() => {
        if (!meetingId || hasLocationSet) {
            setPlaces([]);
            return;
        }
        let cancelled = false;
        setPlacesLoading(true);
        MeetingRepository.onGetFrequentPlaces(meetingId, 5)
            .then((res: any) => {
                if (cancelled) return;
                const list = res?.data?.data || res?.data || [];
                setPlaces(Array.isArray(list) ? list.map((r: any) => ({
                    placeId: String(r.placeId),
                    placeName: r.placeName || t('meetingUpcoming.unnamedPlace'),
                    placeAddress: r.placeAddress,
                    latitude: r.latitude,
                    longitude: r.longitude,
                    visitCount: Number(r.visitCount || 0),
                    overlapMemberCount: Number(r.overlapMemberCount || 0),
                })) : []);
            })
            .catch(() => { if (!cancelled) setPlaces([]); })
            .finally(() => { if (!cancelled) setPlacesLoading(false); });
        return () => { cancelled = true; };
    }, [meetingId, hasLocationSet]);

    const handlePickPlace = async (placeId: string) => {
        if (!meetingId || pickingPlaceId) return;
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostChange') });
            return;
        }
        setPickingPlaceId(placeId);
        try {
            await MeetingRepository.onPutMeetingPlace(meetingId, { placeId });
            showAlert({ message: t('meetingUpcoming.placeSet') });
            onMeetingChanged?.();
        } catch {
            showAlert({ message: t('meetingUpcoming.placeSetFail') });
        } finally {
            setPickingPlaceId(null);
        }
    };

    // 직접 검색해서 장소 설정 — PlaceSearch 진입.
    // 결과는 MeetingDetail 의 useEffect 가 location.state.placeInfo 를 감지해 onPutMeetingPlace 호출.
    const handleOpenPlaceSearch = () => {
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostChange') });
            return;
        }
        navigate('/meeting/place-search', {
            state: {
                returnTo: `/meeting/detail/${meetingId}`,
                upcomingPlaceUpdate: true, // MeetingDetail 가 복귀 시 감지하는 플래그
                meetingId,
                selectedPlace: meeting?.location || '',
                placeInfo: meeting?.location ? {
                    name: meeting.location,
                    address: meeting.locationDetail || '',
                    lat: meeting.latitude,
                    lng: meeting.longitude,
                } : null,
            },
        });
    };

    // ── 장소 추가/수정 — MeetingEdit 와 동일한 SubPlaceModal 재사용
    const [showSubPlaceModal, setShowSubPlaceModal] = useState(false);
    const [editingSubPlace, setEditingSubPlace] = useState<SubPlace | null>(null);
    const [subPlaceFormPrefill, setSubPlaceFormPrefill] = useState<SubPlaceForm | undefined>(undefined);
    // 본 장소 편집 모드 — set 되면 모달이 schedule + meeting.place 를 편집
    const [editingMainPlace, setEditingMainPlace] = useState<{
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
    } | null>(null);

    // PlaceSearch / SubPlaceRecommend 복귀 감지 — 사용자가 거기서 장소 고른 뒤
    // 자동으로 SubPlaceModal 을 다시 열어 등록 흐름을 이어가도록.
    // (MeetingEdit 가 동일한 처리를 하지만 예정 모임 화면에는 없어서 모달이 그냥 닫혀버리던 회귀 수정)
    useEffect(() => {
        const st: any = routeLocation.state;
        if (!st?.fromSubPlaceModal || subPlaceReturnProcessed) return;
        setSubPlaceReturnProcessed(true);

        const placeInfo = st.placeInfo as { name?: string; address?: string; lat?: number; lng?: number; placeId?: string; externalPlaceId?: string } | undefined;
        const savedForm = st.subPlaceForm as SubPlaceForm | undefined;

        // 폼 복원 + 새로 고른 장소 정보 병합
        const restoredForm: SubPlaceForm = {
            ...(savedForm || {
                placeName: '', placeAddress: '', category: 'SECONDARY',
                dayNumber: 1, dayNumbers: [1], visitTime: '', description: '',
            }),
            placeName: placeInfo?.name || savedForm?.placeName || '',
            placeAddress: placeInfo?.address || savedForm?.placeAddress || '',
            ...(typeof placeInfo?.lat === 'number' ? { latitude: placeInfo.lat } : {}),
            ...(typeof placeInfo?.lng === 'number' ? { longitude: placeInfo.lng } : {}),
            ...(placeInfo?.externalPlaceId || placeInfo?.placeId
                ? { placeId: (placeInfo.externalPlaceId || placeInfo.placeId) as string }
                : {}),
        };
        setSubPlaceFormPrefill(restoredForm);

        // 편집 모드면 해당 sub_place 도 set
        if (st.editingSubPlaceId) {
            const editing = (subPlaces || []).find((sp: any) => sp.subPlaceId === st.editingSubPlaceId);
            if (editing) setEditingSubPlace(editing);
        }
        setEditingMainPlace(null);
        setShowSubPlaceModal(true);

        // state 정리 — 같은 path 로 replace 해서 새로고침/뒤로가기 시 다시 트리거되지 않도록
        navigate(routeLocation.pathname, { replace: true, state: {} });
    }, [routeLocation.state, subPlaceReturnProcessed, subPlaces, navigate, routeLocation.pathname]);

    const openAddPlaceModal = () => {
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostAdd') });
            return;
        }
        setEditingSubPlace(null);
        setEditingMainPlace(null);
        setSubPlaceFormPrefill({
            placeName: '',
            placeAddress: '',
            category: isTravelMeeting ? 'ATTRACTION' : 'SECONDARY',
            dayNumber: 1,
            dayNumbers: [1],
            visitTime: '',
            description: '',
        });
        setShowSubPlaceModal(true);
    };

    // 숙소 추가 — 여행 모임 전용. SubPlaceModal 을 HOTEL 카테고리로 미리 열어 검색 시 여행지 좌표 anchor.
    const openAddHotelModal = () => {
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.hotelOnlyHostAdd') });
            return;
        }
        setEditingSubPlace(null);
        setEditingMainPlace(null);
        setSubPlaceFormPrefill({
            placeName: '',
            placeAddress: '',
            category: 'HOTEL',
            dayNumber: 1,
            dayNumbers: [1],
            visitTime: '',
            description: '',
        });
        setShowSubPlaceModal(true);
    };

    const openEditMainPlaceModal = () => {
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostChange') });
            return;
        }
        setEditingSubPlace(null);
        setSubPlaceFormPrefill(undefined);
        setEditingMainPlace({
            placeName: meeting?.location || '',
            placeAddress: meeting?.locationDetail || '',
            latitude: meeting?.latitude ? Number(meeting.latitude) : undefined,
            longitude: meeting?.longitude ? Number(meeting.longitude) : undefined,
        });
        setShowSubPlaceModal(true);
    };

    const handleSaveMainPlace = async (form: {
        placeName: string;
        placeAddress?: string;
        latitude?: number;
        longitude?: number;
        startDatetimeLocal: string;
        endDatetimeLocal: string;
        scheduleChanged: boolean;
    }) => {
        if (!meetingId) return;
        // place 갱신
        await MeetingRepository.onPutMeetingPlace(meetingId, {
            placeName: form.placeName,
            placeAddress: form.placeAddress,
            latitude: form.latitude,
            longitude: form.longitude,
        });
        // schedule 변경됐으면 갱신
        if (form.scheduleChanged && meeting?.confirmedScheduleId) {
            try {
                const { ScheduleRepository } = await import('repository/scheduleRepository');
                await ScheduleRepository.onPutUpdateSchedule(meeting.confirmedScheduleId, {
                    startDatetime: localInputToUtc(form.startDatetimeLocal),
                    endDatetime: localInputToUtc(form.endDatetimeLocal),
                });
            } catch (e) {
                console.warn('[MeetingUpcoming] 모임 일정 변경 실패 — 장소만 변경됨', e);
            }
        }
    };

    const countdown = useMemo(() => {
        if (!startMs) return null;
        const diff = startMs - Date.now();
        if (diff <= 0) return { label: t('meetingUpcoming.countdownNow'), sub: t('meetingUpcoming.countdownNowSub') };
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        if (days > 0) return { label: `D-${days}`, sub: t('meetingUpcoming.countdownHoursLeft', { hours }) };
        if (hours > 0) return { label: t('meetingUpcoming.countdownHM', { hours, minutes }), sub: t('meetingUpcoming.today') };
        return { label: t('meetingUpcoming.countdownMin', { minutes }), sub: t('meetingUpcoming.soonStart') };
    }, [startMs, t]);

    // ── RSVP 집계
    const rsvp = useMemo(() => {
        const list = members || [];
        const total = list.length;
        const attending = list.filter((m: any) => m.attendanceStatus === 'ATTENDING').length;
        const declined = list.filter((m: any) => m.attendanceStatus === 'NOT_ATTENDING').length;
        const undecided = list.filter((m: any) => !m.attendanceStatus || m.attendanceStatus === 'UNDECIDED').length;
        return { total, attending, declined, undecided, responded: attending + declined };
    }, [members]);

    const undecidedNames = useMemo(() => {
        return (members || [])
            .filter((m: any) => !m.attendanceStatus || m.attendanceStatus === 'UNDECIDED')
            .map((m: any) => {
                const raw = m.nickname || m.nickName || '';
                try { return raw ? decodeURIComponent(raw) : `유저${m.userNo}`; } catch { return raw || `유저${m.userNo}`; }
            });
    }, [members]);

    // ── 추가된 장소 목록 (sub_places) — HOTEL 제외, startDatetime / sortOrder 순
    const additionalPlaces = useMemo(() => {
        const list = (subPlaces || []).filter((sp: any) => sp.category !== 'HOTEL');
        return list.sort((a: any, b: any) => {
            const aT = a.startDatetime ? new Date(a.startDatetime).getTime() : Number.MAX_SAFE_INTEGER;
            const bT = b.startDatetime ? new Date(b.startDatetime).getTime() : Number.MAX_SAFE_INTEGER;
            if (aT !== bT) return aT - bT;
            return (a.sortOrder || 0) - (b.sortOrder || 0);
        });
    }, [subPlaces]);

    // ── 숙소 목록 (HOTEL 카테고리) — 여행 모임 전용
    const hotelPlaces = useMemo(() => {
        return (subPlaces || []).filter((sp: any) => sp.category === 'HOTEL');
    }, [subPlaces]);

    const formatSubPlaceTimeRange = (sp: any): string => {
        const fmt = (iso?: string) => {
            if (!iso) return '';
            const d = new Date(iso);
            if (isNaN(d.getTime())) return '';
            const pad = (n: number) => String(n).padStart(2, '0');
            const month = d.getMonth() + 1;
            const date = d.getDate();
            const today = new Date();
            const sameDay = d.getFullYear() === today.getFullYear()
                && d.getMonth() === today.getMonth()
                && d.getDate() === today.getDate();
            const hhmm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
            return sameDay ? hhmm : `${month}/${date} ${hhmm}`;
        };
        const s = fmt(sp.startDatetime);
        const e = fmt(sp.endDatetime);
        if (s && e) return `${s} ~ ${e}`;
        if (s) return s;
        if (sp.visitTime) return sp.visitTime;
        return '';
    };

    const handleEditSubPlace = (sp: any) => {
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostEdit') });
            return;
        }
        setEditingMainPlace(null);
        setSubPlaceFormPrefill(undefined);
        setEditingSubPlace(sp);
        setShowSubPlaceModal(true);
    };

    // 이름 탭 → 그 장소 위치에서 지도 열고 바로 변경 (PlaceSearch anchor=기존 좌표, 편집 모드).
    // 돌아오면 기존 복원 흐름이 SubPlaceModal 을 새 장소로 재오픈 → 저장 시 해당 sub_place 갱신.
    const openPlaceMapForSub = (sp: any) => {
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostChange') });
            return;
        }
        // sub-place 좌표는 API 응답 그대로라 문자열일 수 있음 → Number 로 강제 변환.
        const latNum = Number(sp.latitude);
        const lngNum = Number(sp.longitude);
        const hasCoords = sp.latitude != null && sp.longitude != null
            && !isNaN(latNum) && !isNaN(lngNum) && !(latNum === 0 && lngNum === 0);
        // 좌표 없으면(미지오코딩 등) 여행지 좌표로 fallback → 해외에서 한국 디폴트로 떨어지는 것 방지.
        const anchor = hasCoords
            ? { lat: latNum, lng: lngNum }
            : (travelDestinationCenter && travelDestinationCenter.lat && travelDestinationCenter.lng
                ? { lat: travelDestinationCenter.lat, lng: travelDestinationCenter.lng }
                : null);
        const form: any = {
            placeName: sp.placeName || '',
            placeAddress: sp.placeAddress || '',
            category: sp.category,
            dayNumber: sp.dayNumber || 1,
            dayNumbers: [sp.dayNumber || 1],
            visitTime: sp.visitTime || '',
            description: sp.description || '',
            ...(hasCoords ? { latitude: latNum, longitude: lngNum } : {}),
            ...(sp.placeId ? { placeId: sp.placeId } : {}),
        };
        navigate('/meeting/place-search', {
            state: {
                returnTo: `/meeting/detail/${meetingId}`,
                fromSubPlaceModal: true,
                subPlaceForm: form,
                editingSubPlaceId: sp.subPlaceId,
                meetingId,
                isTravelMeeting,
                // 기존 좌표(또는 여행지)를 anchor → 그 위치에서 지도가 열림 + 해외면 네이티브 구글로 위임.
                placeInfo: anchor
                    ? { name: sp.placeName, address: sp.placeAddress || '', lat: anchor.lat, lng: anchor.lng }
                    : undefined,
            },
        });
    };

    const handleDeleteSubPlace = async (sp: any) => {
        if (!meetingId) return;
        if (!isCreator) {
            showAlert({ message: t('meetingUpcoming.placeOnlyHostDelete') });
            return;
        }
        const ok = await showConfirm({
            title: t('meetingUpcoming.placeDeleteTitle'),
            message: t('meetingUpcoming.placeRemoveMsg', { name: sp.placeName }),
            confirmText: t('meetingUpcoming.delete'),
        });
        if (!ok) return;
        try {
            await MeetingRepository.onDeleteSubPlace(meetingId, sp.subPlaceId);
            showAlert({ message: t('meetingUpcoming.placeDeleted') });
            onMeetingChanged?.();
        } catch {
            showAlert({ message: t('meetingUpcoming.placeDeleteFail') });
        }
    };

    /* 주최자 전용 장소 액션 — 추가·수정·삭제가 세 자리에서 반복돼 한 곳으로 모았습니다 */
    const placeActions = (opts: { onEdit: () => void; onDelete: () => void; deleting?: boolean }) => (
        <span className={styles.rowActions}>
            <button type="button" className={styles.rowAction} onClick={opts.onEdit}>
                {t('meetingUpcoming.edit')}
            </button>
            <button
                type="button"
                className={`${styles.rowAction} ${styles.rowActionDanger}`}
                onClick={opts.onDelete}
                disabled={opts.deleting}
            >
                {opts.deleting ? t('meetingUpcoming.deleting') : t('meetingUpcoming.delete')}
            </button>
        </span>
    );

    return (
        <AppScreen title={meeting?.name || t('meetingUpcoming.meetingFallback')} bar="sub" hasFooter>
            {/* 이 화면에서 검은 판은 하나 — 아직 참여하지 않은 사람에게 지금 눌러야 할 것 */}
            {!isMember && (
                <Section spacing="tight">
                    <InverseBanner
                        title={t('meetingUpcoming.invitedBanner')}
                        sub={isJoining ? t('meetingUpcoming.joining') : t('meetingUpcoming.invitedBannerSub')}
                        onClick={isJoining ? undefined : onJoinMeeting}
                    />
                </Section>
            )}

            {/* ① 무슨 모임이고 언제 어디서 */}
            <Section spacing="tight">
                <div className={styles.tags}>
                    <Badge tone="outline">{t('meetingUpcoming.upcomingBadge')}</Badge>
                    {countdown && <TimeBadge size="sm" urgent={!countdown.label.startsWith('D-')}>{countdown.label}</TimeBadge>}
                    <MeetingTypeTag isRegular={meeting?.isRegularMeeting} />
                </div>
                <h1 className={styles.title}>{meeting?.name}</h1>
                <dl className={styles.facts}>
                    <div className={styles.fact}>
                        <dt className={styles.factLabel}>{t('meetingUpcoming.whenLabel')}</dt>
                        <dd className={styles.factValue}>
                            {(meeting?.date || meeting?.time) ? (
                                <>
                                    {meeting?.date}{meeting?.time ? ` · ${meeting.time}` : ''}
                                    {countdown?.sub && <span className={styles.factSub}>{countdown.sub}</span>}
                                </>
                            ) : (
                                <span className={styles.factPending}>{t('meetingUpcoming.scheduleNotDecided')}</span>
                            )}
                        </dd>
                    </div>
                    <div className={styles.fact}>
                        <dt className={styles.factLabel}>{t('meetingUpcoming.place')}</dt>
                        <dd className={styles.factValue}>
                            {meeting?.location || <span className={styles.factPending}>{t('meetingUpcoming.addPlace')}</span>}
                        </dd>
                    </div>
                </dl>
            </Section>

            {/* ② 일정이 아직 없으면 — 조율하러 가기 */}
            {!meeting?.date && (
                <Section spacing="tight">
                    <ActionList>
                        <ActionRow
                            standalone
                            icon={<IconCalendar size={18} />}
                            title={meeting?.hasActiveProposal
                                ? t('meetingUpcoming.proposalActive')
                                : t('meetingUpcoming.arrangeSchedule')}
                            desc={meeting?.hasActiveProposal
                                ? t('meetingUpcoming.proposalVoteCta', { count: meeting?.proposalVoteCount || 0 })
                                : t('meetingUpcoming.arrangeScheduleDesc')}
                            onClick={() => navigate(`/schedule/main/${meetingId}`)}
                        />
                    </ActionList>
                </Section>
            )}

            {/* ③ 장소 — 이 화면에서 큰 그림은 지도 하나뿐입니다 */}
            <Section divided>
                <SectionHeader title={t('meetingUpcoming.place')} />
                {meeting?.latitude && meeting?.longitude ? (
                    <>
                        <Bleed>
                            <div
                                className={styles.map}
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/meeting/location/${meetingId}`)}
                                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/meeting/location/${meetingId}`); }}
                            >
                                <MapContainer
                                    center={{ lat: Number(meeting.latitude), lng: Number(meeting.longitude) }}
                                    markers={[{
                                        id: 'destination',
                                        lat: Number(meeting.latitude),
                                        lng: Number(meeting.longitude),
                                        label: '',
                                        tooltip: meeting.location,
                                        isDestination: true,
                                    }]}
                                    height={220}
                                    zoomLevel={7}
                                    placeName={meeting.location}
                                    placeAddress={meeting.locationDetail || ''}
                                />
                            </div>
                        </Bleed>
                        <div className={styles.row}>
                            <span
                                className={styles.rowBody}
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/meeting/location/${meetingId}`)}
                                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/meeting/location/${meetingId}`); }}
                            >
                                <span className={styles.rowName}>{meeting.location}</span>
                                {meeting.locationDetail && (
                                    <span className={styles.rowMeta}>{meeting.locationDetail}</span>
                                )}
                            </span>
                            {isCreator && placeActions({
                                onEdit: openEditMainPlaceModal,
                                onDelete: handleClearPlace,
                                deleting: clearingPlace,
                            })}
                        </div>
                        {isCreator && (
                            <Button variant="secondary" block icon={<IconPlus size={16} />} onClick={openAddPlaceModal}>
                                {t('meetingUpcoming.titleAddPlace')}
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        {placesLoading && <p className={styles.state}>{t('meetingUpcoming.loadingPlaces')}</p>}
                        {!placesLoading && places.length > 0 && (
                            <>
                                <p className={styles.hint}>{t('meetingUpcoming.frequentPlacesDesc')}</p>
                                <ul className={styles.list}>
                                    {places.map(p => {
                                        const picking = pickingPlaceId === p.placeId;
                                        return (
                                            <li key={p.placeId} className={styles.row}>
                                                <span className={styles.rowBody}>
                                                    <span className={styles.rowName}>
                                                        {localizePlaceName(p.placeName, i18n.language)}
                                                    </span>
                                                    {p.placeAddress && <span className={styles.rowMeta}>{p.placeAddress}</span>}
                                                    <span className={styles.rowMeta}>
                                                        {t('meetingUpcoming.placeVisitStat', { members: p.overlapMemberCount, visits: p.visitCount })}
                                                    </span>
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    disabled={picking || !isCreator}
                                                    onClick={() => handlePickPlace(p.placeId)}
                                                    title={!isCreator ? t('meetingUpcoming.hostMustSetPlace') : ''}
                                                >
                                                    {picking ? t('meetingUpcoming.setting') : t('meetingUpcoming.pickThis')}
                                                </Button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        )}
                        <Button
                            block
                            icon={<IconPlus size={16} />}
                            disabled={!isCreator}
                            onClick={handleOpenPlaceSearch}
                        >
                            {places.length > 0 ? t('meetingUpcoming.searchOtherPlace') : t('meetingUpcoming.addPlace')}
                        </Button>
                        {!isCreator && <p className={styles.hint}>{t('meetingUpcoming.hostMustSetPlace')}</p>}
                    </>
                )}
            </Section>

            {/* ③-h 숙소 — 여행 모임 전용 */}
            {isTravelMeeting && (
                <Section divided>
                    <SectionHeader title={t('meetingUpcoming.hotelCount', { count: hotelPlaces.length })} />
                    {hotelPlaces.length > 0 ? (
                        <ul className={styles.list}>
                            {hotelPlaces.map((sp: any) => (
                                <li key={sp.subPlaceId} className={styles.row}>
                                    <span className={styles.rowIcon}><IcHotel size={16} /></span>
                                    <span
                                        className={styles.rowBody}
                                        role={isCreator ? 'button' : undefined}
                                        tabIndex={isCreator ? 0 : undefined}
                                        onClick={isCreator ? () => openPlaceMapForSub(sp) : undefined}
                                        onKeyDown={isCreator ? (e) => { if (e.key === 'Enter') openPlaceMapForSub(sp); } : undefined}
                                        title={isCreator ? t('meetingUpcoming.changeOnMap') : undefined}
                                    >
                                        <span className={styles.rowName}>{localizePlaceName(sp.placeName, i18n.language)}</span>
                                        {sp.placeAddress && <span className={styles.rowMeta}>{sp.placeAddress}</span>}
                                    </span>
                                    {isCreator && placeActions({
                                        onEdit: () => handleEditSubPlace(sp),
                                        onDelete: () => handleDeleteSubPlace(sp),
                                    })}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className={styles.state}>
                            {t('meetingUpcoming.noHotel')}{isCreator ? t('meetingUpcoming.registerHint') : ''}
                        </p>
                    )}
                    {isCreator && (
                        <Button variant="secondary" block icon={<IconPlus size={16} />} onClick={openAddHotelModal}>
                            {t('meetingUpcoming.addHotel')}
                        </Button>
                    )}
                </Section>
            )}

            {/* ③-b 추가된 장소 */}
            {(additionalPlaces.length > 0 || isTravelMeeting) && (
                <Section divided>
                    <SectionHeader
                        title={isTravelMeeting
                            ? t('meetingUpcoming.attractionCount', { count: additionalPlaces.length })
                            : t('meetingUpcoming.extraPlaceCount', { count: additionalPlaces.length })}
                    />
                    {additionalPlaces.length === 0 && isTravelMeeting && (
                        <p className={styles.state}>
                            {t('meetingUpcoming.noAttraction')}{isCreator ? t('meetingUpcoming.registerHint') : ''}
                        </p>
                    )}
                    {additionalPlaces.length > 0 && (
                        <ul className={styles.list}>
                            {additionalPlaces.map((sp: any) => {
                                const timeRange = formatSubPlaceTimeRange(sp);
                                const categoryLabel = sp.category === 'SECONDARY' ? t('category.secondary')
                                    : sp.category === 'TERTIARY' ? t('category.tertiary')
                                    : sp.category === 'CAFE' ? t('category.cafe')
                                    : sp.category === 'RESTAURANT' ? t('category.restaurant')
                                    : sp.category === 'ATTRACTION' ? t('category.attraction')
                                    : t('meetingUpcoming.placeGeneric');
                                return (
                                    <li key={sp.subPlaceId} className={styles.row}>
                                        <span
                                            className={styles.rowBody}
                                            role={isCreator ? 'button' : undefined}
                                            tabIndex={isCreator ? 0 : undefined}
                                            onClick={isCreator ? () => openPlaceMapForSub(sp) : undefined}
                                            onKeyDown={isCreator ? (e) => { if (e.key === 'Enter') openPlaceMapForSub(sp); } : undefined}
                                            title={isCreator ? t('meetingUpcoming.changeOnMap') : undefined}
                                        >
                                            <span className={styles.rowTags}>
                                                <Badge tone="outline">{categoryLabel}</Badge>
                                                {timeRange && <TimeBadge size="inline">{timeRange}</TimeBadge>}
                                            </span>
                                            <span className={styles.rowName}>{localizePlaceName(sp.placeName, i18n.language)}</span>
                                            {sp.placeAddress && <span className={styles.rowMeta}>{sp.placeAddress}</span>}
                                        </span>
                                        {isCreator && placeActions({
                                            onEdit: () => handleEditSubPlace(sp),
                                            onDelete: () => handleDeleteSubPlace(sp),
                                        })}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {isTravelMeeting && isCreator && (
                        <Button variant="secondary" block icon={<IconPlus size={16} />} onClick={openAddPlaceModal}>
                            {t('meetingUpcoming.addAttraction')}
                        </Button>
                    )}
                </Section>
            )}

            {/* ④ 누가 오는지 — 색만이 아니라 형태로도 구분합니다 */}
            <Section divided>
                <SectionHeader title={t('meetingUpcoming.rsvpTitle', { responded: rsvp.responded, total: rsvp.total })} />
                <div className={styles.bar} aria-hidden>
                    {rsvp.attending > 0 && <span className={styles.barAttend} style={{ flex: rsvp.attending }} />}
                    {rsvp.undecided > 0 && <span className={styles.barUndecided} style={{ flex: rsvp.undecided }} />}
                    {rsvp.declined > 0 && <span className={styles.barDecline} style={{ flex: rsvp.declined }} />}
                </div>
                <ul className={styles.legend}>
                    <li className={styles.legendItem}>
                        <span className={`${styles.legendMark} ${styles.markAttend}`} aria-hidden />
                        {t('meetingUpcoming.attend')}<strong>{rsvp.attending}</strong>
                    </li>
                    <li className={styles.legendItem}>
                        <span className={`${styles.legendMark} ${styles.markUndecided}`} aria-hidden />
                        {t('meetingUpcoming.noResponse')}<strong>{rsvp.undecided}</strong>
                    </li>
                    <li className={styles.legendItem}>
                        <span className={`${styles.legendMark} ${styles.markDecline}`} aria-hidden />
                        {t('meetingUpcoming.decline')}<strong>{rsvp.declined}</strong>
                    </li>
                </ul>

                {undecidedNames.length > 0 && (
                    <>
                        <p className={styles.hint}>{t('meetingUpcoming.notRespondedYet')}</p>
                        <div className={styles.chips}>
                            {undecidedNames.slice(0, 8).map((n, i) => (
                                <span key={i} className={styles.chip}>{n}</span>
                            ))}
                            {undecidedNames.length > 8 && (
                                <span className={styles.chipMore}>
                                    {t('meetingUpcoming.othersCount', { count: undecidedNames.length - 8 })}
                                </span>
                            )}
                        </div>
                    </>
                )}

                {isMember && (
                    <Button variant="secondary" block onClick={onChangeAttendance}>
                        {t('meetingUpcoming.changeMyResponse')}
                    </Button>
                )}
            </Section>

            {/* ⑤ 자주 함께한 사람 + 친구 초대 */}
            <MeetingInviteSection
                meetingId={meetingId}
                inviteCode={meeting?.inviteCode}
                onShare={onShare}
                onOpenInviteFromPast={onOpenInviteFromPast}
                externallyInvitedUserNos={externallyInvitedUserNos}
            />

            {/* 내 위치 공유 — 참석일 때만 켤 수 있습니다 */}
            {isMember && onToggleLocationSharing && (
                <Section divided>
                    <SwitchList>
                        <SwitchRow
                            label={t('meetingUpcoming.myLocShare')}
                            desc={attendanceStatus !== 'attending' ? t('meetingUpcoming.locShareHint') : undefined}
                            checked={!!isLocationSharing}
                            disabled={attendanceStatus !== 'attending'}
                            onChange={onToggleLocationSharing}
                        />
                    </SwitchList>
                </Section>
            )}

            {/* 참여자 */}
            <Section divided last>
                <SectionHeader title={t('meetingUpcoming.participantsCount', { count: participants.length })} />
                <ul className={styles.list}>
                    {participants.map((p: any) => {
                        const myUserNo = Number(sessionStorage.getItem('userNo')) || 0;
                        const targetUserNo = Number(p.userNo);
                        const canAct = !!targetUserNo && targetUserNo > 0 && targetUserNo !== myUserNo;
                        return (
                            <li key={p.id || p.userNo} className={styles.row}>
                                <Avatar
                                    size="sm"
                                    person={{
                                        userNo: p.userNo || p.id || 0,
                                        name: p.name || p.nickname || '?',
                                        imageUrl: p.profileImgUrl,
                                        attendance: p.status === 'confirmed' ? 'going'
                                            : p.status === 'declined' ? 'declined' : 'pending',
                                        isHost: !!p.isCreator,
                                    }}
                                    statusLabel={p.status === 'confirmed' ? t('meetingUpcoming.attend')
                                        : p.status === 'declined' ? t('meetingUpcoming.decline')
                                            : t('meetingUpcoming.undecided')}
                                />
                                <span className={styles.rowBody}>
                                    <span className={styles.rowName}>
                                        {p.name || p.nickname}
                                        {p.isCreator && <span className={styles.rowHost}>{t('meetingUpcoming.host')}</span>}
                                    </span>
                                    {p.status === 'confirmed' && (
                                        <span className={styles.rowMeta}>
                                            {p.locationSharing
                                                ? t('meetingUpcoming.locSharing')
                                                : t('meetingUpcoming.locSharingOff')}
                                        </span>
                                    )}
                                </span>
                                <span className={
                                    p.status === 'confirmed' ? `${styles.rsvp} ${styles.rsvpAttend}`
                                        : p.status === 'declined' ? `${styles.rsvp} ${styles.rsvpDecline}`
                                            : `${styles.rsvp} ${styles.rsvpUndecided}`
                                }>
                                    {p.status === 'confirmed' ? t('meetingUpcoming.attend')
                                        : p.status === 'declined' ? t('meetingUpcoming.decline')
                                            : t('meetingUpcoming.undecided')}
                                </span>
                                {canAct && (
                                    <MemberActionMenu
                                        targetUserNo={targetUserNo}
                                        targetName={p.name || p.nickname || `User #${targetUserNo}`}
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
            </Section>

            {/* 장소 추가/수정 — MeetingEdit 와 공통 SubPlaceModal */}
            {showSubPlaceModal && meetingId && (
                <SubPlaceModal
                    isOpen={showSubPlaceModal}
                    onClose={() => {
                        setShowSubPlaceModal(false);
                        setEditingSubPlace(null);
                        setSubPlaceFormPrefill(undefined);
                        setEditingMainPlace(null);
                    }}
                    meetingId={meetingId}
                    editingSubPlace={editingSubPlace}
                    isTravelMeeting={isTravelMeeting}
                    travelDestinationName={travelDestinationName}
                    travelDestinationCenter={travelDestinationCenter}
                    travelPeriod={travelPeriod || null}
                    meetingTime={meeting?.meetingTime || meeting?.time || ''}
                    subPlaces={subPlaces || []}
                    onSaveSuccess={() => {
                        setShowSubPlaceModal(false);
                        setEditingSubPlace(null);
                        setSubPlaceFormPrefill(undefined);
                        setEditingMainPlace(null);
                        onMeetingChanged?.();
                    }}
                    returnTo={`/meeting/detail/${meetingId}`}
                    initialForm={subPlaceFormPrefill}
                    meetingScheduleStartDatetime={meeting?.scheduleStartDatetime}
                    meetingScheduleEndDatetime={meeting?.scheduleEndDatetime}
                    meetingScheduleId={meeting?.confirmedScheduleId}
                    mainPlaceName={meeting?.location}
                    editingMainPlace={editingMainPlace}
                    onSaveMainPlace={handleSaveMainPlace}
                />
            )}

            <ButtonFooter>
                {isMember && (
                    <Button
                        variant="ghost"
                        onClick={isCreator ? handleDeleteMeeting : handleLeaveMeeting}
                        disabled={leavingOrDeleting}
                    >
                        {isCreator ? t('meetingUpcoming.delete') : t('meetingUpcoming.leaveBtn')}
                    </Button>
                )}
                {isCreator && (
                    <Button variant="secondary" onClick={onEdit}>
                        {t('meetingUpcoming.editBtn')}
                    </Button>
                )}
                <Button block onClick={() => navigate(`/chat/room/${meetingId}`)}>
                    {t('meetingUpcoming.chat')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};

export default MeetingUpcomingView;
